'use client'

import React, { useState, useEffect } from 'react'
import { X, Star, Loader2, MessageSquare } from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { submitKRScore, listKRScores, EvaluatorScore } from '@/api/okr-evaluation'
import { toast } from 'sonner'

interface KRScoringDialogProps {
  krId: string
  krName: string
  isOpen: boolean
  onClose: () => void
  onScoreSubmitted?: () => void
}

export const KRScoringDialog: React.FC<KRScoringDialogProps> = ({
  krId,
  krName,
  isOpen,
  onClose,
  onScoreSubmitted
}) => {
  const [score, setScore] = useState<number>(0)
  const [hoveredScore, setHoveredScore] = useState<number>(0)
  const [comments, setComments] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [existingScore, setExistingScore] = useState<EvaluatorScore | null>(null)
  const [averageScore, setAverageScore] = useState<number | null>(null)
  const { tenant } = useTenant()

  useEffect(() => {
    if (isOpen && tenant?.domain) {
      loadExistingScore()
    }
  }, [isOpen, krId, tenant?.domain])

  const loadExistingScore = async () => {
    if (!tenant?.domain) return

    try {
      setLoading(true)
      const response = await listKRScores(tenant.domain, krId)
      setAverageScore(response.averageScore)

      // Find current user's score if exists
      // Note: We'd need to get current user ID from context
      // For now, just load the first score as example
      if (response.scores.length > 0) {
        const myScore = response.scores[0] // TODO: Filter by current user
        setExistingScore(myScore)
        setScore(myScore.score)
        setComments(myScore.comments || '')
      }
    } catch (error) {
      console.error('Erro ao carregar pontuação:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (score === 0) {
      toast.error('Por favor, selecione uma pontuação')
      return
    }

    if (!tenant?.domain) return

    setSubmitting(true)

    try {
      const result = await submitKRScore(tenant.domain, krId, {
        score,
        comments: comments.trim() || undefined
      })

      toast.success('Pontuação registrada com sucesso!')
      setAverageScore(result.averageScore)
      onScoreSubmitted?.()
      onClose()
    } catch (error) {
      console.error('Erro ao registrar pontuação:', error)
      toast.error('Falha ao registrar pontuação')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-brand-dark-light border border-brand-gray/10 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-brand-gray/10">
          <h2 className="text-xl font-semibold text-white">
            Avaliar Resultado-Chave
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-brand-dark-lighter rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-brand-gray" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-brand-gray" />
            </div>
          ) : (
            <>
              {/* KR Name */}
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Resultado-Chave
                </label>
                <p className="text-white">{krName}</p>
              </div>

              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-3">
                  Pontuação (1-5)
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((starValue) => (
                    <button
                      key={starValue}
                      type="button"
                      onClick={() => setScore(starValue)}
                      onMouseEnter={() => setHoveredScore(starValue)}
                      onMouseLeave={() => setHoveredScore(0)}
                      className="transition-transform hover:scale-110 focus:outline-none"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          starValue <= (hoveredScore || score)
                            ? 'fill-yellow-400 text-warning'
                            : 'text-brand-gray'
                        }`}
                      />
                    </button>
                  ))}
                  {score > 0 && (
                    <span className="ml-4 text-lg font-semibold text-white">
                      {score}/5
                    </span>
                  )}
                </div>
                <div className="mt-2 text-sm text-brand-gray">
                  {score === 1 && '1 = Não atingiu'}
                  {score === 2 && '2 = Parcialmente atingido'}
                  {score === 3 && '3 = Atingido'}
                  {score === 4 && '4 = Superou'}
                  {score === 5 && '5 = Excepcional'}
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Comentários (opcional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Adicione observações sobre esta avaliação..."
                  rows={4}
                  className="w-full px-3 py-2 bg-brand-dark-lighter border border-brand-gray/20 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 placeholder:text-brand-gray"
                />
              </div>

              {/* Average Score Display */}
              {averageScore !== null && (
                <div className="bg-brand-dark-lighter/50 border border-brand-gray/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 fill-yellow-400 text-warning" />
                    <span className="text-sm font-medium text-white">
                      Média atual: {averageScore.toFixed(2)}/5
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-brand-gray/10 bg-brand-dark-light rounded-b-lg">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-brand-dark-lighter border border-brand-gray/20 rounded-md hover:bg-brand-dark disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || score === 0}
            className="px-4 py-2 text-sm font-medium text-black bg-success rounded-md hover:bg-success disabled:opacity-50 flex items-center space-x-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{submitting ? 'Registrando...' : existingScore ? 'Atualizar' : 'Registrar Avaliação'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
